import { CommonModule, ViewportScroller } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  HostBinding,
  input,
  OnChanges,
  OnInit,
  output,
  SimpleChanges,
  ViewChild,
  inject,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { EcommerceTrackingService, RoutingService } from '@boels/helpers';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faLocationDot, faSearch } from '@fortawesome/free-solid-svg-icons';
import { NgSelectModule } from '@ng-select/ng-select';
import { TRANSLOCO_SCOPE, TranslocoModule } from '@jsverse/transloco';
import { take } from 'rxjs';
import { TextInputComponent } from '../../form/input/text-input.component';
import { StoreFinderSpecialismDropdownComponent } from '../store-finder-specialism-dropdown/store-finder-specialism-dropdown.component';
import { CultureFacade, StoreFinderFacade } from '@boels/shared-state';
import { ButtonComponent } from '../../button/button.component';
import { VerticalAlignerComponent } from '../../vertical-aligner/vertical-aligner.component';

@Component({
  selector: 'boels-store-finder-search',
  templateUrl: './store-finder-search.component.html',
  styleUrls: ['./store-finder-search.component.scss'],
  imports: [
    CommonModule,
    TranslocoModule,
    FontAwesomeModule,
    ReactiveFormsModule,
    TextInputComponent,
    NgSelectModule,
    StoreFinderSpecialismDropdownComponent,
    ButtonComponent,
    VerticalAlignerComponent,
  ],
  providers: [
    {
      provide: TRANSLOCO_SCOPE,
      useValue: ['', 'storeFinder'],
    },
  ],
})
export class StoreFinderSearchComponent
  implements OnChanges, OnInit, AfterViewInit
{
  private router = inject(Router);
  private viewportScroller = inject(ViewportScroller);
  private routingService = inject(RoutingService);
  private storeFinderFacade = inject(StoreFinderFacade);
  private ecommerceTrackingService = inject(EcommerceTrackingService);
  private cd = inject(ChangeDetectorRef);
  private cultureFacade = inject(CultureFacade);

  @ViewChild('searchInput') searchInput: TextInputComponent;

  inputChanged = output<string>();

  doSearch = input<boolean>(false);
  inputValue = input<string | null>(null);
  isHomepage = input<boolean>(false);

  searchBox = new FormControl<string>('');

  protected readonly faLocationDot = faLocationDot;
  protected readonly faSearch = faSearch;

  @HostBinding('class.-homepage') get homepageClass(): boolean {
    return this.isHomepage();
  }

  ngOnInit(): void {
    this.storeFinderFacade.setActiveRegion(null);

    if (this.inputValue()) {
      this.searchBox.setValue(this.inputValue());
    } else {
      const queryParams = this.routingService.getRouteProperty('queryParams');
      this.updateInput(queryParams?.['query']);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.['doSearch']?.currentValue) {
      this.findStores(this.searchBox.value as string);
    }
  }

  ngAfterViewInit() {
    this.cd.detectChanges();
  }

  findStores(address: string): void {
    this.cultureFacade
      .getActiveCulture()
      .pipe(take(1))
      .subscribe((culture: string) => {
        const queryParams = this.routingService.getRouteProperty('queryParams');
        const url = this.router.createUrlTree([culture, 'branches', 'find'], {
          queryParams: {
            query: address,
            specialism: queryParams?.['specialism'],
          },
        });

        this.ecommerceTrackingService.setStoreLocatorSearchEvent(address);
        this.router.navigateByUrl(url);
      });
  }

  viewStoresWithMyLoc(): void {
    this.cultureFacade
      .getActiveCulture()
      .pipe(take(1))
      .subscribe((culture: string) => {
        const queryParams = this.routingService.getRouteProperty('queryParams');
        const url = this.router.createUrlTree(
          ['', culture, 'branches', 'find'],
          {
            queryParams: {
              useMyLocation: true,
              specialism: queryParams?.['specialism'],
            },
          },
        );

        this.ecommerceTrackingService.setStoreLocatorSearchEvent(
          'useMyLocation',
        );
        this.router.navigateByUrl(url);
      });
  }

  scrollToAnchor(elementId: string): void {
    this.viewportScroller.scrollToAnchor(elementId);
  }

  updateInput(inputValue: string): void {
    this.searchBox.patchValue(inputValue);
  }

  onKey(event: KeyboardEvent, inputValue: string): void {
    if (
      this.searchBox.value &&
      this.searchBox.value.length &&
      event.key === 'Enter'
    ) {
      this.searchBox.patchValue(inputValue);
      this.findStores(this.searchBox.value);
    }
    this.inputChanged.emit(this.searchBox.value as string);
  }
}
